/**
 * 온습도로거 업무 흐름 — 설정 가이드 '업무 흐름도' 카테고리 (2026-11-05).
 *
 * 세 장의 다이어그램(React Flow — 프로젝트에 이미 있는 @xyflow/react, 새 의존성 없음):
 *   ① 업무 흐름도   — 담당자(lab) · 현장 · 기기 · 시스템 4개 레인 × 준비→설치→수집→확인→조치→활용 단계 (스윔레인)
 *   ② 작업 순서     — 사용자가 실제로 하는 9단계를 번호 순으로(지점 등록 → 장치 등록 → … → 기록 활용), 수신 실패 분기 포함
 *   ③ 데이터 흐름   — 센서 → 기기 로그 → LTE-M/1NCE → 수신기 → 백엔드 → 지점 기록 → 화면 · 엑셀 · 알림 (ACK 되돌림)
 * Mermaid 대신 React Flow 를 쓴 이유: 노드를 누르면 그 화면(탭) · 가이드 카테고리로 바로 이동하고, 선택한 단계의 앞뒤 연결을
 * 강조하며, 앱 테마(토큰 색 · Pretendard)와 같은 모양을 낸다. Mermaid 는 정적 SVG 라 이 연결이 어렵고 번들이 수 MB 늘어난다.
 * 노드 이동 · 연결 편집은 막았다(문서용). 확대/축소 · 끌어서 보기만 허용한다.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlow, Background, Controls, Handle, Position, MarkerType, type Node, type Edge, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Segmented, Tag } from 'antd';
import { ArrowRightOutlined, BookOutlined } from '@ant-design/icons';
import { gray } from '../theme/tokens';

/* 가이드 → 페이지 탭 · 가이드 카테고리 이동 (ThLoggerGuide 가 제공) */
export type GuideNav = { goTab?: (key: string) => void; goGuide: (key: string, aud?: 'user' | 'dev') => void };
export const GuideNavContext = createContext<GuideNav>({ goGuide: () => {} });

const TAB_LABEL: Record<string, string> = {
  overview: '현황', data: '측정 데이터', points: '설치 지점', devices: '장치 대장', uplinks: '업링크 로그', rejects: '거절 로그',
};
const GUIDE_LABEL: Record<string, string> = {
  siminstall: '유심 장착 · 기기 열기', at: 'AT 명령 · 블루투스', ustart: '시작하기', point: '지점 연속성 · 기기 교체',
  rxlog: '수신 확인 · 로그', buy: '구매 구성', pcsim: 'PC 시뮬레이터', interval: '전송 간격 · 시각', sim: '1NCE 구매 절차', device: '장치 설정 · 시뮬레이터',
};

/* 레인 = 누가 하는가. 색만으로 구분하지 않게 레인 이름 · 아이콘 글자를 함께 쓴다 */
const LANES = [
  { key: 'admin', label: '담당자', sub: 'lab 화면', c: '#4338ca', soft: '#eef2ff', line: '#c7d2fe' },
  { key: 'field', label: '현장', sub: '하우스', c: '#0f766e', soft: '#f0fdfa', line: '#99f6e4' },
  { key: 'device', label: '기기', sub: 'S31-CB 자동', c: '#b45309', soft: '#fffbeb', line: '#fde68a' },
  { key: 'system', label: '시스템', sub: '서버 자동', c: '#475569', soft: '#f8fafc', line: '#cbd5e1' },
] as const;
type LaneKey = typeof LANES[number]['key'];
const laneOf = (k: LaneKey) => LANES.find(l => l.key === k)!;

type Step = {
  no?: number; title: string; sub: string; lane: LaneKey; kind?: 'decision' | 'auto';
  tab?: string; guide?: string; aud?: 'user' | 'dev';
  todo: string[]; done: string;
};

/* 단계 정의 — 세 다이어그램이 같은 정의를 공유한다(문구가 어긋나지 않게) */
const S: Record<string, Step> = {
  point: { no: 1, title: '설치 지점 등록', sub: '하우스 · 위치 = 기록의 주인', lane: 'admin', tab: 'points',
    todo: ['설치 지점 탭 → 지점 등록', '코드는 현장 표지와 같게(예 H01-1) · 이름 · 위치 · 메모', '위치를 옮기면 같은 코드를 고치지 말고 새 지점'],
    done: '지점 목록에 \'기기 없음\' 상태로 보인다' },
  device: { no: 2, title: '장치 등록 (IMEI)', sub: '라벨의 15자리 = 수신 허용', lane: 'admin', tab: 'devices',
    todo: ['장치 대장 탭 → 등록', '기기 라벨의 IMEI 15자리 · 장치명(지명-번호) · 주기 1일 1회 · 동봉 32', '등록하지 않은 IMEI 는 서버가 저장하지 않고 거절 로그에 남긴다'],
    done: '장치 대장에 \'미수신\' 으로 보인다' },
  sim: { no: 3, title: '유심 장착 · AT 설정', sub: '블루투스 앱 · 08:00 ATZ', lane: 'field', guide: 'at',
    todo: ['1NCE 유심 장착(전원 끄고) → 전원 켜기', '휴대폰 앱으로 블루투스 연결(라벨 PIN 6자리)', '설정 가이드의 권장 AT 명령 입력 → 08:00 무렵 ATZ'],
    done: '버튼 3초 → 초록 5번 → 파랑 3초(망 접속)' },
  install: { no: 4, title: '지점에 기기 설치', sub: '지점 → 설치 → 기기 선택', lane: 'admin', tab: 'points',
    todo: ['설치 지점 탭 → 해당 지점 \'설치\'', '장치 대장의 기기 중 하나를 고르고 설치 시각 입력', '한 지점 = 한 기기 = 한 시점 (겹치면 거절)'],
    done: '지점에 기기 이름이 붙고 현황 카드가 생긴다' },
  field: { no: 5, title: '하우스 설치 · 첫 업로드', sub: '버튼 1~3초 → 2분 안에 확인', lane: 'field', guide: 'rxlog',
    todo: ['설치 전 기준 온습도계와 나란히 두고 값 비교 · 편차 기록(시험 기록용)', '하우스 중앙 · 작물 높이 · 직사광 가림(복사 차폐)', '버튼 1~3초 → 파랑 1번 = 즉시 업로드 → lab 업링크 로그에 309B 행 · 알림센터 \'첫 수신\''],
    done: '업링크 로그에 행이 생긴다 (안 보이면 수신 확인 · 로그)' },
  assign: { no: 6, title: '시험 배정', sub: '지점 ↔ 시험번호 · 기간', lane: 'admin', tab: 'points',
    todo: ['설치 지점 → 배정 → 시험번호와 기간', '배정 기간의 측정값이 성적서 환경조건 근거가 된다', '기기를 바꿔도 지점 배정은 이어진다'],
    done: '현황 카드 · 측정 데이터에 시험번호 태그' },
  measure: { title: '1시간 측정 · 매일 08:00 업로드', sub: '최근 32건 동봉 · 유실 시 재전송', lane: 'device', kind: 'auto',
    todo: ['정시마다 온습도를 기기 안에 기록', '매일 08:00 에 최근 32건을 UDP+HEX 309B 로 전송', 'ACK 를 못 받으면 다음 업로드에 다시 보낸다(REDPT)'],
    done: '사람이 할 일 없음' },
  ingest: { title: '수신 · 해석 · 지점 기록', sub: '중복 제거 · 결손은 결손대로', lane: 'system', kind: 'auto', tab: 'uplinks',
    todo: ['수신기가 원문을 백엔드로 전달 → 해석', '등록 · 활성 기기만 저장, 아니면 거절 로그', '(기기, 시각) 중복 제거 · 설치 이력으로 지점 기록을 만든다'],
    done: '업링크 로그 · 측정 데이터에 반영' },
  watch: { title: '수신 판정 · 알림', sub: '30분마다 · 지연 / 미수신 / 배터리', lane: 'system', kind: 'auto',
    todo: ['마지막 업로드 경과로 판정: TDC+1h 주의 · 2주기 심각', '배터리 3.0V 미만 경고 · 첫 수신 안내', 'Discord · 알림센터로 통지'],
    done: '알림이 오면 현황에서 확인' },
  daily: { no: 7, title: '매일 현황 확인', sub: '아침 1~2분', lane: 'admin', tab: 'overview',
    todo: ['현황 탭의 \'확인 필요\' 필터', '카드의 현재값 · 48h 그래프 · 수신 · 배터리 · 신호', '장치 대장의 14일 수신 띠로 누락 · 지연 확인'],
    done: '모든 카드가 정상이면 끝' },
  judge: { no: 8, title: '이상이 있나?', sub: '지연 · 배터리 · 값 이상', lane: 'admin', kind: 'decision', tab: 'overview',
    todo: ['수신 지연 하루는 기다린다(다음 08:00 에 재전송으로 회수)', '이틀째 미수신 · 배터리 교체 · 신호 약함이면 현장 조치', '값이 이상하면 프로브 위치 · 차폐 확인'],
    done: '정상 → 기록 활용 / 이상 → 현장 조치' },
  fix: { no: 8, title: '현장 조치 · 기기 교체', sub: '교체는 설치 지점에서 기록', lane: 'field', guide: 'point',
    todo: ['LED · 안테나 · 위치 · 배터리 확인', '교체: 예비기 설치 → 설치 지점 \'교체\'(사유 · 병행 측정)', '교체 사이 공백은 결손으로 남는다(메우지 않는다)'],
    done: '같은 지점 기록이 새 기기로 이어진다' },
  use: { no: 9, title: '기록 활용', sub: '측정 데이터 · 엑셀 · 성적서', lane: 'admin', tab: 'data',
    todo: ['측정 데이터 탭 → 지점 · 기간(배정 시험 기간 프리셋) → 평균 · 최저 · 최고 · 결손', '엑셀 = 성적서 환경조건 원자료(값마다 측정 기기 열). 결손 구간은 결손으로 기재 — 보간하지 않는다', '수신 원문은 서버에 보관 — 감사 때 업링크 로그에서 원문 ↔ 해석 대조'],
    done: '시험 종료 → 배정 종료 · 지점 철거 기록, 기기는 예비로' },
  rx: { title: '수신 확인 · 로그', sub: '기기 → 1NCE → 서버 구간별', lane: 'admin', guide: 'rxlog', kind: 'decision',
    todo: ['거절 로그에 IMEI 가 있으면 등록 · 활성 문제', '없으면 1NCE 포털 접속 기록 → 서버 도착 수 → 수신기 로그', '구간을 좁혀 원인 조치'],
    done: '업링크 로그에 행이 생기면 6단계로' },
};

/* ── 노드 ─────────────────────────────────────────────── */
type NData = { step?: Step; id: string; sel: boolean; dim: boolean; w?: number };
const hs: React.CSSProperties = { opacity: 0, width: 6, height: 6, minWidth: 0, minHeight: 0, border: 0 };
const Sides: React.FC = () => (
  <>
    {(['Top', 'Right', 'Bottom', 'Left'] as const).map(p => (
      <React.Fragment key={p}>
        <Handle type="source" position={Position[p]} id={`s${p[0]}`} style={hs} />
        <Handle type="target" position={Position[p]} id={`t${p[0]}`} style={hs} />
      </React.Fragment>))}
  </>
);
const StepNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as unknown as NData; const st = d.step!; const L = laneOf(st.lane);
  const decision = st.kind === 'decision'; const auto = st.kind === 'auto';
  return (
    <div className="thl-flow-node" style={{
      width: d.w || 150, minHeight: 66, padding: '7px 9px 8px', borderRadius: decision ? 18 : 9, cursor: 'pointer',
      background: auto ? L.soft : '#fff', border: `${d.sel ? 2.5 : 1.5}px ${auto ? 'dashed' : 'solid'} ${d.sel ? L.c : L.line}`,
      boxShadow: d.sel ? `0 0 0 4px ${L.c}22, 0 4px 12px rgba(15,23,42,.12)` : '0 1px 3px rgba(15,23,42,.08)',
      opacity: d.dim ? 0.55 : 1, transition: 'opacity .15s, box-shadow .15s', position: 'relative',
    }}>
      <Sides />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {st.no != null && <span style={{ flex: 'none', width: 19, height: 19, borderRadius: 10, background: L.c, color: '#fff', fontSize: 11, fontWeight: 800,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{st.no}</span>}
        {decision && <span style={{ flex: 'none', fontSize: 12, fontWeight: 800, color: L.c }}>◆</span>}
        {auto && <span style={{ flex: 'none', fontSize: 10, fontWeight: 700, color: L.c, border: `1px solid ${L.line}`, borderRadius: 4, padding: '0 3px', background: '#fff' }}>자동</span>}
        <b style={{ fontSize: 13, color: gray[900], lineHeight: 1.3, wordBreak: 'keep-all' }}>{st.title}</b>
      </div>
      <div style={{ fontSize: 11.5, color: gray[600], marginTop: 3, lineHeight: 1.35, wordBreak: 'keep-all' }}>{st.sub}</div>
      {(st.tab || st.guide) && (
        <div style={{ marginTop: 4, fontSize: 10.5, color: L.c, fontWeight: 700 }}>
          {st.tab ? `▸ ${TAB_LABEL[st.tab]} 탭` : `▸ 가이드: ${GUIDE_LABEL[st.guide!]}`}
        </div>)}
    </div>
  );
};
/* 레인 배경 + 왼쪽 이름표 */
const LaneNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as unknown as { lane: LaneKey; w: number; h: number }; const L = laneOf(d.lane);
  return (
    <div style={{ width: d.w, height: d.h, background: L.soft, border: `1px solid ${L.line}`, borderRadius: 10, display: 'flex' }}>
      <div style={{ width: 74, flex: 'none', borderRight: `1px solid ${L.line}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        background: '#fff', borderRadius: '10px 0 0 10px' }}>
        <b style={{ fontSize: 13, color: L.c }}>{L.label}</b>
        <span style={{ fontSize: 10.5, color: gray[500] }}>{L.sub}</span>
      </div>
    </div>
  );
};
const PhaseNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as unknown as { label: string; w: number };
  return <div style={{ width: d.w, textAlign: 'center', fontSize: 12, fontWeight: 800, color: gray[600], borderBottom: `2px solid ${gray[300]}`, paddingBottom: 3 }}>{d.label}</div>;
};
const nodeTypes = { step: StepNode, lane: LaneNode, phase: PhaseNode };

/* ── 배치 ─────────────────────────────────────────────── */
type Spec = { nodes: Node[]; edges: Edge[]; h: number };
const E = (id: string, s: string, t: string, sh: string, th: string, label?: string, o: Partial<Edge> = {}): Edge => ({
  id, source: s, target: t, sourceHandle: sh, targetHandle: th, label, type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: gray[500] },
  style: { stroke: gray[500], strokeWidth: 1.6 }, labelStyle: { fontSize: 10.5, fill: gray[700], fontWeight: 600 },
  labelBgStyle: { fill: '#fff' }, labelBgPadding: [3, 2], ...o,
});
const step = (id: string, x: number, y: number, w = 150): Node => ({ id, type: 'step', position: { x, y }, data: { id, step: S[id], w } as any, draggable: false, connectable: false });

const swimlane = (): Spec => {
  const COL = 150, X0 = 82, LH = 124, Y0 = 30, W = X0 + COL * 7 + 4;
  const lanes: LaneKey[] = ['admin', 'field', 'device', 'system'];
  const ly = (k: LaneKey) => Y0 + lanes.indexOf(k) * (LH + 8);
  const at = (id: string, col: number) => step(id, X0 + col * COL + 5, ly(S[id].lane) + 10, 138);
  const nodes: Node[] = [
    ...lanes.map(k => ({ id: `lane-${k}`, type: 'lane', position: { x: 0, y: ly(k) }, data: { lane: k, w: W, h: LH }, draggable: false, selectable: false, zIndex: -1 } as Node)),
    ...([['준비', 0, 2], ['설치', 2, 1], ['수집', 3, 1], ['확인', 4, 1], ['조치', 5, 1], ['활용', 6, 1]] as const).map(([l, c, n]) =>
      ({ id: `ph-${l}`, type: 'phase', position: { x: X0 + c * COL + 5, y: 0 }, data: { label: l, w: COL * n - 12 }, draggable: false, selectable: false } as Node)),
    at('point', 0), at('device', 1), at('sim', 1), at('install', 2), at('field', 2), at('assign', 3), at('measure', 3), at('ingest', 3),
    at('watch', 4), at('daily', 4), at('judge', 5), at('fix', 5), at('use', 6),
  ];
  const auto = { animated: true, style: { stroke: '#b45309', strokeWidth: 1.8 }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#b45309' } };
  const edges = [
    E('e1', 'point', 'device', 'sR', 'tL'), E('e2', 'device', 'sim', 'sB', 'tT', '기기 전달'), E('e3', 'sim', 'install', 'sR', 'tL'),
    E('e4', 'install', 'field', 'sB', 'tT', '현장 설치'), E('e5', 'field', 'measure', 'sB', 'tL', '업로드', auto as any),
    E('e6', 'install', 'assign', 'sR', 'tL'), E('e7', 'measure', 'ingest', 'sB', 'tT', 'UDP+HEX', auto as any),
    E('e8', 'ingest', 'watch', 'sR', 'tL'), E('e9', 'watch', 'daily', 'sT', 'tB', '알림'), E('e10', 'ingest', 'daily', 'sR', 'tB', '화면 반영', { style: { stroke: gray[400], strokeWidth: 1.3, strokeDasharray: '4 3' } }),
    E('e11', 'assign', 'daily', 'sR', 'tL'), E('e12', 'daily', 'judge', 'sR', 'tL'), E('e13', 'judge', 'use', 'sR', 'tL', '정상'),
    E('e14', 'judge', 'fix', 'sB', 'tT', '이상', { style: { stroke: '#dc2626', strokeWidth: 1.8 }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#dc2626' }, labelStyle: { fontSize: 10.5, fill: '#b91c1c', fontWeight: 700 } }),
    E('e15', 'fix', 'install', 'sL', 'tB', '교체 기록', { style: { stroke: '#0f766e', strokeWidth: 1.5, strokeDasharray: '5 4' }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#0f766e' } }),
  ];
  return { nodes, edges, h: Y0 + 4 * (LH + 8) };
};

const ordered = (): Spec => {
  // 위 줄 왼→오 1~5, 아래 줄 오→왼 6~9 (뱀 모양) + 5단계 수신 실패 분기
  const COL = 172, W = 150, Y1 = 10, Y2 = 190;
  const nodes = [
    step('point', 0, Y1, W), step('device', COL, Y1, W), step('sim', COL * 2, Y1, W), step('install', COL * 3, Y1, W), step('field', COL * 4, Y1, W),
    { ...step('rx', COL * 5 + 10, Y1, W) },
    step('assign', COL * 4, Y2, W), step('daily', COL * 3, Y2, W), step('judge', COL * 2, Y2, W), step('use', COL * 1, Y2, W), step('fix', COL * 2, Y2 + 150, W),
  ];
  const edges = [
    E('o1', 'point', 'device', 'sR', 'tL'), E('o2', 'device', 'sim', 'sR', 'tL'), E('o3', 'sim', 'install', 'sR', 'tL'), E('o4', 'install', 'field', 'sR', 'tL'),
    E('o5', 'field', 'rx', 'sR', 'tL', '안 보이면', { style: { stroke: '#dc2626', strokeWidth: 1.5, strokeDasharray: '5 4' }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#dc2626' } }),
    E('o5b', 'rx', 'field', 'sB', 'tB', '조치 후 다시'),
    E('o6', 'field', 'assign', 'sB', 'tT', '수신 성공'), E('o7', 'assign', 'daily', 'sL', 'tR'), E('o8', 'daily', 'judge', 'sL', 'tR'),
    E('o9', 'judge', 'use', 'sL', 'tR', '정상'), E('o10', 'judge', 'fix', 'sB', 'tT', '이상', { style: { stroke: '#dc2626', strokeWidth: 1.6 }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#dc2626' } }),
    E('o11', 'fix', 'daily', 'sR', 'tB', '다음 날 다시 확인', { style: { stroke: '#0f766e', strokeWidth: 1.5, strokeDasharray: '5 4' } }),
  ];
  return { nodes, edges, h: Y2 + 240 };
};

/* 데이터 흐름 — 전용 단계(설명은 기술 위주) */
const DF: Record<string, Step> = {
  sensor: { title: 'SHT31 프로브', sub: '온도 ±0.3℃ · 습도 ±2%', lane: 'device', kind: 'auto', guide: 'buy', aud: 'dev', todo: ['외장 프로브 3m · 복사 차폐 권장'], done: '' },
  log: { title: 'S31-CB 기기 로그', sub: '1시간 기록 · CLOCKLOG', lane: 'device', kind: 'auto', guide: 'at', todo: ['AT+CLOCKLOG=1,65535,60,32', '매일 08:00 최근 32건 + 현재값'], done: '' },
  net: { title: 'LTE-M · 1NCE', sub: 'KT 망 로밍(1NCE 서면 확인 전)', lane: 'field', kind: 'auto', guide: 'sim', aud: 'dev', todo: ['AT+IOTMOD=0 · AT+APN=iot.1nce.net', '사용량 · 접속 기록은 1NCE 포털'], done: '' },
  rcv: { title: '수신기 UDP 1999', sub: '원문 그대로 전달 · ACK 반환', lane: 'system', kind: 'auto', guide: 'rxlog', todo: ['sappt-thlogger-receiver (Docker)', '백엔드 2xx 일 때만 ACK'], done: '' },
  api: { title: '백엔드 해석', sub: '등록 확인 · 중복 제거', lane: 'system', kind: 'auto', tab: 'uplinks', todo: ['HEX 309B = 헤더 53B + 8B × 32', '미등록 · 비활성 · 해석 실패 → 거절 로그'], done: '' },
  db: { title: '지점 기록 (DB)', sub: '설치 이력으로 지점 값 조합', lane: 'system', kind: 'auto', tab: 'points', todo: ['thl_readings (기기, 시각) 유니크', '지점 값은 조회 시 설치 이력으로 조인'], done: '' },
  scr: { title: '현황 · 측정 데이터', sub: '카드 · 그래프 · 통계', lane: 'admin', tab: 'overview', todo: ['현황 카드 · 48h 그래프', '측정 데이터 · 결손 통계'], done: '' },
  xls: { title: '엑셀 · 성적서', sub: '환경조건 원자료', lane: 'admin', tab: 'data', todo: ['측정 기기 열 포함 내보내기'], done: '' },
  alert: { title: 'Discord · 알림센터', sub: '지연 · 미수신 · 배터리', lane: 'admin', kind: 'auto', todo: ['30분마다 수신 판정'], done: '' },
};
const dataflow = (): Spec => {
  // 두 줄 뱀 모양 — 위 줄: 현장 → 망 → 수신기(왼 → 오), 아래 줄: 서버 → 화면(오 → 왼). 한 줄 7칸이면 1366 에서 글자가 8px 대로 줄었다
  const COL = 190, W = 150, Y1 = 30, Y2 = 210;
  const mk = (id: string, x: number, y: number): Node => ({ id, type: 'step', position: { x, y }, data: { id, step: DF[id], w: W } as any, draggable: false, connectable: false });
  const nodes = [mk('sensor', 0, Y1), mk('log', COL, Y1), mk('net', COL * 2, Y1), mk('rcv', COL * 3, Y1),
    mk('api', COL * 3, Y2), mk('db', COL * 2, Y2), mk('scr', COL * 1 - 10, Y2 - 55), mk('xls', COL * 1 - 10, Y2 + 55), mk('alert', COL * 2, Y2 + 140)];
  const flow = { animated: true, style: { stroke: '#4338ca', strokeWidth: 1.8 }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#4338ca' } } as any;
  const edges = [
    E('d1', 'sensor', 'log', 'sR', 'tL', '1시간', flow), E('d2', 'log', 'net', 'sR', 'tL', '309B', flow), E('d3', 'net', 'rcv', 'sR', 'tL', 'UDP', flow),
    E('d4', 'rcv', 'api', 'sB', 'tT', 'HTTP', flow), E('d5', 'api', 'db', 'sL', 'tR', '', flow), E('d6', 'db', 'scr', 'sL', 'tR', '', flow), E('d7', 'db', 'xls', 'sL', 'tR', '', flow),
    E('d8', 'db', 'alert', 'sB', 'tT', '30분 판정'),
    E('d9', 'rcv', 'log', 'sT', 'tT', 'ACK — 없으면 다음 날 재전송(REDPT)', { style: { stroke: '#b45309', strokeWidth: 1.5, strokeDasharray: '5 4' }, markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#b45309' }, labelStyle: { fontSize: 10.5, fill: '#b45309', fontWeight: 700 } }),
  ];
  return { nodes, edges, h: Y2 + 230 };
};

const VIEWS = {
  swim: { label: '업무 흐름도', desc: '누가 · 어느 단계에서 · 무엇을 하는가 — 담당자 · 현장 · 기기 · 시스템 4개 레인', make: swimlane, first: 'point' },
  order: { label: '작업 순서', desc: '사용자가 하는 9단계 — 지점 등록에서 기록 활용까지, 수신 실패 · 이상 분기 포함', make: ordered, first: 'point' },
  data: { label: '데이터 흐름', desc: '측정값이 센서에서 화면 · 엑셀 · 알림까지 가는 길 — ACK 가 없으면 기기가 다음 날 다시 보낸다', make: dataflow, first: 'log' },
} as const;
type ViewKey = keyof typeof VIEWS;

const FlowSection: React.FC = () => {
  const nav = useContext(GuideNavContext);
  const [view, setView] = useState<ViewKey>('swim');
  const [sel, setSel] = useState<string>('point');
  const spec = useMemo(() => VIEWS[view].make(), [view]);
  const steps = view === 'data' ? DF : S;
  const cur = steps[sel] ? sel : VIEWS[view].first;
  const st = steps[cur];
  // 선택한 단계와 연결된 것만 또렷하게
  const linked = useMemo(() => {
    const s = new Set<string>([cur]);
    spec.edges.forEach(e => { if (e.source === cur) s.add(e.target); if (e.target === cur) s.add(e.source); });
    return s;
  }, [spec, cur]);
  const nodes = spec.nodes.map(n => n.type === 'step' ? { ...n, data: { ...(n.data as any), sel: n.id === cur, dim: !linked.has(n.id) } } : n);
  const edges = spec.edges.map(e => {
    const on = e.source === cur || e.target === cur;
    return { ...e, zIndex: on ? 2 : 1, style: { ...e.style, opacity: on ? 1 : 0.45, strokeWidth: on ? 2.4 : (e.style as any)?.strokeWidth } };
  });
  const L = laneOf(st.lane);
  // 폭이 좁으면(1366 등) 상세 패널을 아래로 내려 다이어그램 폭을 확보한다 — 오른쪽에 두면 배율이 0.5 아래로 떨어져 글자를 못 읽는다
  const boxRef = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(true);
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setWide(el.clientWidth >= 1180)); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boxRef}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <Segmented value={view} onChange={v => { setView(v as ViewKey); setSel(VIEWS[v as ViewKey].first); }}
          options={(Object.keys(VIEWS) as ViewKey[]).map(k => ({ value: k, label: VIEWS[k].label }))} />
        <span style={{ fontSize: 12, color: gray[600] }}>{VIEWS[view].desc}</span>
        <span style={{ fontSize: 11.5, color: gray[500], marginLeft: 'auto' }}>단계를 누르면 할 일 · 화면 이동 · 확대는 오른쪽 위 버튼</span>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexDirection: wide ? 'row' : 'column' }}>
        <div className="thl-flow" style={{ flex: wide ? 1 : 'none', minWidth: 0, height: Math.max(340, Math.min(wide ? 520 : 480, spec.h + 30)), border: `1px solid ${gray[200]}`, borderRadius: 10, background: '#fff' }}>
          <ReactFlow key={`${view}-${wide}`} nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView fitViewOptions={{ padding: { top: 0.12, bottom: 0.05, left: 0.04, right: 0.06 }, maxZoom: 1 }} minZoom={0.4} maxZoom={1.6}
            nodesDraggable={false} nodesConnectable={false} elementsSelectable={false} zoomOnScroll={false} preventScrolling={false} panOnScroll={false}
            zoomOnDoubleClick={false} onNodeClick={(_, n) => { if (n.type === 'step') setSel(n.id); }}>
            <Background gap={18} size={1} color={gray[200]} />
            <Controls showInteractive={false} position="top-right" />
          </ReactFlow>
        </div>
        {/* 선택한 단계 — 할 일 · 완료 기준 · 바로가기 */}
        <aside className="thl-flow-detail" style={{ width: wide ? 250 : 'auto', flex: 'none', border: `1px solid ${L.line}`, borderTop: `4px solid ${L.c}`, borderRadius: 10, padding: '10px 12px', background: '#fff', fontSize: 12.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Tag color={undefined} style={{ margin: 0, color: L.c, borderColor: L.line, background: L.soft, fontWeight: 700 }}>{L.label}</Tag>
            {st.no != null && <span style={{ fontSize: 11.5, color: gray[500] }}>{st.no}단계</span>}
            {st.kind === 'auto' && <span style={{ fontSize: 11.5, color: gray[500] }}>자동 — 사람이 할 일 없음</span>}
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: gray[900], lineHeight: 1.35 }}>{st.title}</div>
          <div style={{ fontSize: 12, color: gray[500], marginBottom: 8 }}>{st.sub}</div>
          <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.7, color: gray[700], columns: wide ? 1 : 2, columnGap: 24 }}>{st.todo.map(t => <li key={t}>{t}</li>)}</ul>
          {st.done && <div style={{ marginTop: 8, padding: '6px 8px', background: gray[50], borderRadius: 6, color: gray[700] }}><b style={{ color: L.c }}>완료 기준</b> · {st.done}</div>}
          <div style={{ display: 'flex', flexDirection: wide ? 'column' : 'row', gap: 6, marginTop: 10 }}>
            {st.tab && nav.goTab && <Button type="primary" size="small" icon={<ArrowRightOutlined />} onClick={() => nav.goTab!(st.tab!)}>{TAB_LABEL[st.tab]} 탭 열기</Button>}
            {st.guide && <Button size="small" icon={<BookOutlined />} onClick={() => nav.goGuide(st.guide!, st.aud)}>가이드: {GUIDE_LABEL[st.guide]}</Button>}
          </div>
        </aside>
      </div>
      {/* 범례 — 색만으로 구분하지 않는다: 레인 이름 · 테두리 모양(실선 = 사람, 점선 = 자동) · ◆ 판단 */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: 11.5, color: gray[600] }}>
        {LANES.map(l => <span key={l.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: l.soft, border: `1.5px solid ${l.c}` }} /><b style={{ color: l.c }}>{l.label}</b> {l.sub}</span>)}
        <span>│ 실선 상자 = 사람이 하는 일 · 점선 상자 <b>자동</b> = 기기 · 서버 · ◆ = 판단 · 번호 = 작업 순서 · 움직이는 선 = 데이터</span>
      </div>
    </div>
  );
};

export default FlowSection;
